import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { API_URL, fetchJson } from "@/lib/admin-api";
import { formatWeight } from "@/lib/format";

/**
 * The events behind the device sheet's last direct and last DataBridge capture (POS2-1436): their
 * weights and photo state, refreshed while a device is being switched. Weight the Scales block
 * shows with no newer capture is one the logger did not make.
 */

export interface LastEvent {
  logged_at: string;
  item_weight: number;
  raw_weight: number | null;
  photo: "photo" | "pending" | "none";
}

const PHOTO = { photo: "photo", pending: "photo pending", none: "no photo" } as const;

/** Kilograms to the gram, as the Scales block shows them, so the two compare. */
export function kg(value: number): string {
  return formatWeight("en", value, 3);
}

export function weights(event: LastEvent): string {
  const raw = event.raw_weight === null ? "—" : kg(event.raw_weight);
  return `item ${kg(event.item_weight)} · raw ${raw} · ${PHOTO[event.photo]}`;
}

export function useLastEvents(device: { id: string; retired: boolean }) {
  const headers = useApiHeaders();
  return useQuery<{ device: LastEvent | null; imported: LastEvent | null }, Error>({
    queryKey: ["admin", "devices", device.id, "last-events"],
    queryFn: () => fetchJson(`${API_URL}/api/v1/admin/devices/${device.id}/last-events`, headers),
    retry: false,
    // A retired device answers 409; its rows keep the list's times.
    enabled: !device.retired,
    // Watched while a device is being switched.
    refetchInterval: 30_000,
  });
}
